import React, { useState } from 'react';
import cogoToast from 'cogo-toast';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';

import { getCatalogScriptArtifact } from '../../api/catalogs';
import { downloadTextFile, openTextInTab } from './catalogArtifactFiles';
import './CatalogScriptManager.scss';

const CatalogScriptManager = ({ brandKey, BrandIntegration_id, entries, integrations, isLoading, isUploading, onRename, onUpload }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [busyKeys, setBusyKeys] = useState(new Set());
  const unnamedCount = entries.filter(entry => !entry.name).length;

  const fetchScript = async (entry, mode) => {
    const busyKey = `${entry.fingerprint}-${mode}`;
    setBusyKeys(current => new Set(current).add(busyKey));
    try {
      const artifact = await getCatalogScriptArtifact(brandKey, BrandIntegration_id, entry.fingerprint);
      if (mode === 'download') return downloadTextFile(`${brandKey}-script-${entry.fingerprint}.py`, artifact.content);
      openTextInTab(artifact.content);
    } catch (artifactError) {
      cogoToast.error(artifactError, { hideAfter: 10 });
    } finally {
      setBusyKeys(current => {
        const next = new Set(current);
        next.delete(busyKey);
        return next;
      });
    }
  };
  return (
    <div className='catalogs-script-manager'>
      <div className='catalogs-script-manager-header'>
        <div className='catalogs-script-manager-heading'>
          <h2>Scripts</h2>
          <div className={`catalogs-script-manager-intro${unnamedCount ? ' attention' : ''}`}>
            {scriptSummary({ entries, isLoading, unnamedCount })}
          </div>
        </div>
        <div className='catalogs-script-manager-actions'>
          {entries.length > 0 && (
            <button
              aria-expanded={isExpanded}
              className='catalogs-button ghost compact'
              onClick={() => setIsExpanded(expanded => !expanded)}
            >
              {isExpanded ? 'Hide scripts' : 'Show scripts'}
            </button>
          )}
          <button className='catalogs-button secondary compact' disabled={isUploading} onClick={onUpload}>
            {isUploading ? <><FontAwesomeIcon className='catalogs-loading-icon' icon={faSpinner} spin />Uploading</> : 'Upload script'}
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className='catalogs-script-manager-list'>
          {entries.map(entry => (
            <ScriptRow
              key={entry.fingerprint}
              busyKeys={busyKeys}
              entry={entry}
              otherLiveDomains={otherLiveDomains({ entry, integrations, BrandIntegration_id })}
              onFetch={fetchScript}
              onRename={onRename}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ScriptRow = ({ busyKeys, entry, otherLiveDomains, onFetch, onRename }) => {
  const [draftName, setDraftName] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const save = async () => {
    const name = String(draftName || '').trim();
    if (!name || name === (entry.name || '')) return setDraftName(null);
    setIsSaving(true);
    try {
      if (await onRename(entry.fingerprint, name)) setDraftName(null);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className='catalogs-script-manager-row'>
      {draftName == null ? (
        <div className='catalogs-script-manager-name'>
          {entry.name ? <b>{entry.name}</b> : <i>{`Unnamed script · ${entry.fingerprint.slice(0, 8)}`}</i>}
          {entry.isLive && <span className='catalogs-script-manager-live'>Live</span>}
        </div>
      ) : (
        <input
          autoFocus
          aria-label='Script name'
          maxLength={80}
          value={draftName}
          onChange={event => setDraftName(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') save();
            if (event.key === 'Escape') setDraftName(null);
          }}
        />
      )}
      <div className='catalogs-script-manager-meta'>
        <span>{metaLabel(entry)}</span>
        {otherLiveDomains.map(domain => (
          <span key={domain}>{`${entry.isLive ? 'Also live' : 'Live'} on ${domain}`}</span>
        ))}
      </div>
      <div className='catalogs-script-manager-row-actions'>
        <button
          className='catalogs-button ghost compact'
          disabled={busyKeys.has(`${entry.fingerprint}-view`)}
          onClick={() => onFetch(entry, 'view')}
        >
          {busyKeys.has(`${entry.fingerprint}-view`) ? 'Loading' : 'View'}
        </button>
        <button
          className='catalogs-button ghost compact'
          disabled={busyKeys.has(`${entry.fingerprint}-download`)}
          onClick={() => onFetch(entry, 'download')}
        >
          {busyKeys.has(`${entry.fingerprint}-download`) ? 'Loading' : 'Download'}
        </button>
        {draftName == null ? (
          <button className='catalogs-button ghost compact' onClick={() => setDraftName(entry.name || '')}>Rename</button>
        ) : (
          <button className='catalogs-button compact' disabled={isSaving} onClick={save}>{isSaving ? 'Saving' : 'Save'}</button>
        )}
      </div>
    </div>
  );
};

const otherLiveDomains = ({ entry, integrations, BrandIntegration_id }) =>
  (integrations || [])
    .filter(integration => Number(integration.id) !== Number(BrandIntegration_id) && integration.live?.scriptFingerprint === entry.fingerprint)
    .map(integration => integration.domain);

const scriptSummary = ({ entries, isLoading, unnamedCount }) => {
  if (isLoading) return 'Loading scripts…';
  if (!entries.length) return 'No scripts stored for this brand yet.';
  if (unnamedCount) return `${unnamedCount} unnamed ${unnamedCount === 1 ? 'script' : 'scripts'} — add a name so ${unnamedCount === 1 ? 'it is' : 'they are'} easy to recognize later.`;
  const liveScript = entries.find(entry => entry.isLive);
  return `${entries.length} ${entries.length === 1 ? 'script' : 'scripts'} stored${liveScript?.name ? ` · Live: ${liveScript.name}` : ''}`;
};

const metaLabel = entry =>
  [entry.uploadedAt && `Uploaded ${formatStamp(entry.uploadedAt)}`, entry.lastModifiedAt && `Last used ${formatStamp(entry.lastModifiedAt)}`]
    .filter(Boolean)
    .join(' · ') || '—';

const formatStamp = value => moment(value).format('MMM D, h:mm A');

export default CatalogScriptManager;
